import React, { useState, useEffect, useMemo, useRef } from 'react';
import { pdfjs } from 'react-pdf';
import { Citation } from './HighlightedPdfViewer';

// Use the same pdfjs instance as react-pdf to avoid version conflicts
const pdfjsLib = pdfjs;

// Fuzzy text matching utilities
const normalizeText = (text: string): string => {
  return text
    // Normalize quotes
    .replace(/[""'']/g, '"')
    .replace(/['']/g, "'")
    // Normalize dashes
    .replace(/[—–−]/g, '-')
    // Normalize ellipsis
    .replace(/…/g, '...')
    // Handle common OCR issues
    .replace(/fl/g, 'fi')  // OCR confusion
    .replace(/rn/g, 'm')   // OCR confusion
    .replace(/cl/g, 'd')   // OCR confusion
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

const createFuzzyVariants = (text: string): string[] => {
  const variants = [text];
  
  // Add variant without common prefixes/suffixes
  const withoutPrefixes = text.replace(/^(the|a|an)\s+/i, '');
  if (withoutPrefixes !== text) variants.push(withoutPrefixes);
  
  // Add variant with relaxed punctuation
  const noPunctuation = text.replace(/[.,;:!?]/g, '');
  if (noPunctuation !== text) variants.push(noPunctuation);
  
  // Add variant with single spaces
  const singleSpaces = text.replace(/\s+/g, ' ');
  if (singleSpaces !== text) variants.push(singleSpaces);
  
  // Remove duplicates using Array.from for better compatibility
  return Array.from(new Set(variants));
};

const splitCitationIntoFragments = (text: string): string[] => {
  // Split on common citation fragment separators
  const fragments = text
    .split(/(?:\.\.\.|…|\[\.\.\.|\.\s*\.\s*\.|\s*—\s*|\s*–\s*|\s*-{2,}\s*)/)
    .map(fragment => fragment.trim())
    .filter(fragment => fragment.length > 3); // Only keep meaningful fragments
  
  // If no fragments found, treat as single fragment
  if (fragments.length <= 1) {
    return [text];
  }
  
  console.log(`📝 Split citation into ${fragments.length} fragments:`, fragments);
  return fragments;
};

const findBestFragmentMatches = async (
  pdfDoc: any,
  fragments: string[],
  pageNum: number
): Promise<{positions: TextPosition[], totalConfidence: number}> => {
  const allPositions: TextPosition[] = [];
  let totalConfidence = 0;
  let foundFragments = 0;

  for (const fragment of fragments) {
    if (fragment.length < 4) continue; // Skip very short fragments
    
    const fragmentPositions = await findTextInPdf(pdfDoc, fragment, pageNum);
    if (fragmentPositions.length > 0) {
      allPositions.push(...fragmentPositions);
      totalConfidence += Math.max(...fragmentPositions.map(p => p.confidence || 0.5));
      foundFragments++;
    }
  }

  // Calculate average confidence weighted by fragments found
  const avgConfidence = foundFragments > 0 ? totalConfidence / foundFragments : 0;
  
  // Boost confidence if we found multiple fragments
  const fragmentBonus = foundFragments > 1 ? 0.1 * (foundFragments - 1) : 0;
  const finalConfidence = Math.min(1.0, avgConfidence + fragmentBonus);

  return {
    positions: allPositions,
    totalConfidence: finalConfidence
  };
};

// Helper function to calculate string similarity using Levenshtein distance
const calculateStringSimilarity = (str1: string, str2: string): number => {
  if (str1.length === 0) return str2.length === 0 ? 1 : 0;
  if (str2.length === 0) return 0;

  const matrix: number[][] = [];
  
  // Initialize first row and column
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  // Fill the matrix
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1,     // insertion
          matrix[i - 1][j] + 1      // deletion
        );
      }
    }
  }

  const maxLength = Math.max(str1.length, str2.length);
  const distance = matrix[str2.length][str1.length];
  return 1 - (distance / maxLength);
};

const calculateMatchConfidence = (original: string, found: string, matchType: string): number => {
  const baseConfidence = {
    'exact': 1.0,
    'multi_fragment': 0.95,
    'normalized': 0.9,
    'fuzzy': 0.85,
    'first_sentence': 0.75,
    'key_phrase': 0.65,
    'unique_word': 0.5,
    'number': 0.7,
    'partial_word': 0.45,
    'proximity': 0.4,
    'fuzzy_char': 0.35,
    'last_resort': 0.25
  };

  const base = baseConfidence[matchType as keyof typeof baseConfidence] ?? 0.5;

  // Exact / normalized / fuzzy whole-string matches are scored on length ratio
  // because they're meant to cover the full quote.
  if (matchType === 'exact' || matchType === 'normalized' || matchType === 'fuzzy' || matchType === 'multi_fragment') {
    const lengthRatio = Math.min(found.length, original.length) / Math.max(found.length, original.length);
    return base * lengthRatio;
  }

  // Fragment-style strategies (key phrase, unique word, number, partial word,
  // proximity) match a CHUNK of the quote — we shouldn't punish them just
  // because that chunk is shorter than the full sentence. Award the base
  // confidence with a mild bonus for longer chunks.
  const chunkBonus = Math.min(0.2, found.length / 200);
  return Math.min(0.95, base + chunkBonus);
};

const findContextPositions = (
  textItems: TextItem[],
  mainPositions: TextPosition[],
  contextChars: number = 50
): TextPosition[] => {
  const contextPositions: TextPosition[] = [];
  
  if (mainPositions.length === 0) return contextPositions;
  
  // Find the text item indexes that contain our main highlights
  const highlightedIndexes = new Set<number>();
  mainPositions.forEach(pos => {
    textItems.forEach((item, index) => {
      // Check if this text item overlaps with the highlight position
      if (Math.abs(item.y - (pos.y + pos.height)) < item.height && 
          item.x >= pos.x - 10 && item.x <= pos.x + pos.width + 10) {
        highlightedIndexes.add(index);
      }
    });
  });
  
  // For each highlighted index, find context before and after
  highlightedIndexes.forEach(idx => {
    let contextBefore = '';
    let contextAfter = '';
    let charsBefore = 0;
    let charsAfter = 0;
    
    // Collect context before
    for (let i = idx - 1; i >= 0 && charsBefore < contextChars; i--) {
      if (Math.abs(textItems[i].y - textItems[idx].y) > textItems[idx].height * 1.5) {
        break; // Different line
      }
      contextBefore = textItems[i].text + contextBefore;
      charsBefore += textItems[i].text.length;
      
      // Add this item as context
      contextPositions.push({
        page: mainPositions[0].page,
        x: textItems[i].x,
        y: mainPositions[0].y, // Use same y as main highlight
        width: textItems[i].width,
        height: textItems[i].height,
        isContext: true
      });
    }
    
    // Collect context after
    for (let i = idx + 1; i < textItems.length && charsAfter < contextChars; i++) {
      if (Math.abs(textItems[i].y - textItems[idx].y) > textItems[idx].height * 1.5) {
        break; // Different line
      }
      contextAfter += textItems[i].text;
      charsAfter += textItems[i].text.length;
      
      // Add this item as context
      contextPositions.push({
        page: mainPositions[0].page,
        x: textItems[i].x,
        y: mainPositions[0].y, // Use same y as main highlight
        width: textItems[i].width,
        height: textItems[i].height,
        isContext: true
      });
    }
  });
  
  return contextPositions;
};

interface TextPosition {
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  confidence?: number;
  matchType?: string;
  isContext?: boolean;
}

interface TextItem {
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  startIndex: number;
  endIndex: number;
}

export const findTextInPdf = async (
  pdfDoc: any, // PDFDocumentProxy from pdfjs
  searchText: string,
  pageNum: number
): Promise<TextPosition[]> => {
  try {
    const page = await pdfDoc.getPage(pageNum);
    
    // Check text content cache
    const textCacheKey = `${pdfDoc._pdfUrl || 'default'}-page-${pageNum}`;
    let textContent;
    
    if (textContentCache.has(textCacheKey)) {
      textContent = textContentCache.get(textCacheKey);
      console.log(`✨ Using cached text content for page ${pageNum}`);
    } else {
      textContent = await page.getTextContent();
      textContentCache.set(textCacheKey, textContent);
      
      // Clean up old cache entries
      if (textContentCache.size > 50) {
        const firstKey = textContentCache.keys().next().value;
        textContentCache.delete(firstKey);
      }
    }
    
    const viewport = page.getViewport({ scale: 1.0 });
    
    const positions: TextPosition[] = [];
    let currentIndex = 0;
    
    // Strategy 0: Multi-fragment citation search (check if text contains fragments)
    const fragments = splitCitationIntoFragments(searchText);
    if (fragments.length > 1) {
      console.log(`🔄 Attempting multi-fragment search for ${fragments.length} fragments`);
      const fragmentResult = await findBestFragmentMatches(pdfDoc, fragments, pageNum);
      
      if (fragmentResult.positions.length > 0) {
        console.log(`✅ Multi-fragment search found ${fragmentResult.positions.length} positions with ${Math.round(fragmentResult.totalConfidence * 100)}% confidence`);
        
        // Update confidence for all positions
        fragmentResult.positions.forEach(pos => {
          pos.confidence = fragmentResult.totalConfidence;
          pos.matchType = 'multi_fragment';
        });
        
        return mergeOverlappingHighlights(fragmentResult.positions);
      }
    }
    
    // Build full page text and track positions
    const textItems: TextItem[] = textContent.items.map((item: any) => {
      const text = item.str;
      const transform = item.transform;
      
      // Calculate position from transform matrix
      const x = transform[4];
      const y = transform[5];
      const width = item.width || (text.length * (transform[0] || 12) * 0.5); // Estimate if not provided
      const height = transform[0] || 12; // Font size from transform
      
      const startIdx = currentIndex;
      currentIndex += text.length;
      
      return {
        text,
        x,
        y,
        width,
        height,
        startIndex: startIdx,
        endIndex: currentIndex
      };
    });
    
    // Search for the text
    const fullPageText = textItems.map(item => item.text).join('');
    const normalizedSearchText = normalizeText(searchText);
    const normalizedPageText = normalizeText(fullPageText);
    
    const addHighlightPositions = (startIdx: number, endIdx: number, confidence: number, matchType: string) => {
      textItems.forEach((item: TextItem) => {
        if (item.startIndex < endIdx && item.endIndex > startIdx) {
          positions.push({
            page: pageNum,
            x: item.x,
            y: viewport.height - item.y - item.height, // Convert to CSS coordinates
            width: item.width,
            height: item.height,
            confidence,
            matchType
          });
        }
      });
    };
    
    // Strategy 1: Exact match
    let searchIndex = fullPageText.toLowerCase().indexOf(searchText.toLowerCase());
    
    if (searchIndex !== -1) {
      console.log(`✅ Found exact match at index ${searchIndex}`);
      const endIndex = searchIndex + searchText.length;
      const confidence = calculateMatchConfidence(searchText, searchText, 'exact');
      addHighlightPositions(searchIndex, endIndex, confidence, 'exact');
    } else {
      // Strategy 1.5: Fuzzy normalized match
      searchIndex = normalizedPageText.indexOf(normalizedSearchText);
      
      if (searchIndex !== -1) {
        console.log(`✅ Found fuzzy normalized match`);
        const endIndex = searchIndex + normalizedSearchText.length;
        const confidence = calculateMatchConfidence(searchText, normalizedSearchText, 'fuzzy');
        addHighlightPositions(searchIndex, endIndex, confidence, 'fuzzy');
      } else {
        // Strategy 1.6: Try fuzzy variants
        const fuzzyVariants = createFuzzyVariants(normalizedSearchText);
        let foundFuzzyMatch = false;
        
        for (const variant of fuzzyVariants) {
          searchIndex = normalizedPageText.indexOf(variant);
          if (searchIndex !== -1) {
            console.log(`✅ Found fuzzy variant match: "${variant}"`);
            const endIndex = searchIndex + variant.length;
            const confidence = calculateMatchConfidence(searchText, variant, 'fuzzy');
            addHighlightPositions(searchIndex, endIndex, confidence, 'fuzzy');
            foundFuzzyMatch = true;
            break;
          }
        }
        
        // Strategy 2: Legacy normalized search (fallback)
        const searchLower = searchText.toLowerCase();
        const pageLower = fullPageText.toLowerCase();
        const normalizedSearch = searchLower.replace(/\s+/g, ' ').trim();
        const normalizedPage = pageLower.replace(/\s+/g, ' ');
        searchIndex = normalizedPage.indexOf(normalizedSearch);
        
        if (searchIndex !== -1) {
          console.log(`✅ Found legacy normalized match`);
          // Map back to original text positions
          let originalPos = 0;
          let normalizedPos = 0;
          
          while (normalizedPos < searchIndex && originalPos < fullPageText.length) {
            if (pageLower[originalPos] === ' ' && normalizedPage[normalizedPos] === ' ') {
              normalizedPos++;
              originalPos++;
            } else if (pageLower[originalPos] === ' ') {
              originalPos++;
            } else {
              normalizedPos++;
              originalPos++;
            }
          }
          
          const startIdx = originalPos;
          const endIdx = startIdx + searchText.length;
          const confidence = calculateMatchConfidence(searchText, normalizedSearch, 'normalized');
          addHighlightPositions(startIdx, endIdx, confidence, 'normalized');
        } else {
          // Strategy 3: First sentence/phrase search
          console.log(`   Trying first sentence search...`);
          const firstSentence = searchText.match(/^[^.!?]+/)?.[0] || searchText.substring(0, 50);
          const sentenceIndex = pageLower.indexOf(firstSentence.toLowerCase());
          
          if (sentenceIndex !== -1) {
            console.log(`✅ Found first sentence match`);
            const endIdx = sentenceIndex + firstSentence.length;
            const confidence = calculateMatchConfidence(searchText, firstSentence, 'first_sentence');
            addHighlightPositions(sentenceIndex, endIdx, confidence, 'first_sentence');
          }
          
          // Strategy 3.5: High-signal number search — insurance quotes are
          // dominated by dollar amounts that appear verbatim on the source
          // page; a "$4,000,000" hit is way more diagnostic than a 2-word
          // phrase. Run this BEFORE the generic key-phrase scan so we don't
          // settle for a low-information match while a precise dollar amount
          // is sitting on the page.
          if (positions.length === 0) {
            const numberMatches = searchText.match(/\$?[\d][\d,]*(?:\.\d+)?/g);
            if (numberMatches && numberMatches.length > 0) {
              // Pull only numbers worth searching for — skip lone 1–2 digit
              // values that match noise (page numbers, paragraph IDs, etc.).
              const meaningful = Array.from(new Set(numberMatches.filter(n => n.replace(/[^\d]/g, '').length >= 3)));
              for (const number of meaningful) {
                const numberIndex = pageLower.indexOf(number.toLowerCase());
                if (numberIndex !== -1) {
                  console.log(`✅ Found high-signal number match: "${number}"`);
                  const endIdx = numberIndex + number.length;
                  const confidence = calculateMatchConfidence(searchText, number, 'number');
                  addHighlightPositions(numberIndex, endIdx, confidence, 'number');
                  break;
                }
              }
            }
          }

          // Strategy 4: Key phrase search (find longest substring)
          if (positions.length === 0) {
            console.log(`   Trying key phrase search...`);
            const words = searchText.split(/\s+/);
            for (let len = Math.min(5, words.length); len >= 2; len--) {
              for (let start = 0; start <= words.length - len; start++) {
                const phrase = words.slice(start, start + len).join(' ').toLowerCase();
                const phraseIndex = pageLower.indexOf(phrase);

                if (phraseIndex !== -1) {
                  console.log(`✅ Found phrase match: "${phrase}"`);
                  const endIdx = phraseIndex + phrase.length;
                  const confidence = calculateMatchConfidence(searchText, phrase, 'key_phrase');
                  addHighlightPositions(phraseIndex, endIdx, confidence, 'key_phrase');
                  break;
                }
              }
              if (positions.length > 0) break;
            }
          }
        
          // Strategy 5: Unique word search
          if (positions.length === 0) {
            console.log(`   Trying unique word search...`);
            const words = searchText.split(/\s+/);
            const uniqueWords = words.filter(word => 
              word.length > 4 && 
              !['the', 'and', 'for', 'with', 'this', 'that', 'from', 'policy', 'coverage'].includes(word.toLowerCase())
            );
            
            for (const uniqueWord of uniqueWords) {
              const wordIndex = pageLower.indexOf(uniqueWord.toLowerCase());
              if (wordIndex !== -1) {
                console.log(`✅ Found unique word match: "${uniqueWord}"`);
                const endIdx = wordIndex + uniqueWord.length;
                const confidence = calculateMatchConfidence(searchText, uniqueWord, 'unique_word');
                addHighlightPositions(wordIndex, endIdx, confidence, 'unique_word');
                break; // Found one unique word, that's enough
              }
            }
          }
        
          // Strategy 6: Number-specific search (for citations with amounts)
          if (positions.length === 0) {
            console.log(`   Trying number-specific search...`);
            const numberMatches = searchText.match(/[\d,]+\.?\d*/g);
            if (numberMatches && numberMatches.length > 0) {
              for (const number of numberMatches) {
                const numberIndex = pageLower.indexOf(number.toLowerCase());
                if (numberIndex !== -1) {
                  console.log(`✅ Found number match: "${number}"`);
                  const endIdx = numberIndex + number.length;
                  const confidence = calculateMatchConfidence(searchText, number, 'number');
                  addHighlightPositions(numberIndex, endIdx, confidence, 'number');
                  break;
                }
              }
            }
          }
          
          // Strategy 7: Partial word matching (for OCR errors)
          if (positions.length === 0) {
            console.log(`   Trying partial word matching...`);
            const words = searchText.split(/\s+/).filter(w => w.length > 3);
            let bestMatch = { index: -1, score: 0, text: '', matchType: 'partial_word' };
            
            for (const word of words) {
              // Try variations with common OCR substitutions
              const variations = [
                word.replace(/rn/g, 'm'),
                word.replace(/vv/g, 'w'),
                word.replace(/cl/g, 'd'),
                word.replace(/fi/g, 'fl'),
                word.replace(/o/g, '0'),
                word.replace(/0/g, 'o'),
                word.replace(/l/g, 'I'),
                word.replace(/I/g, 'l')
              ];
              
              for (const variation of variations) {
                const idx = pageLower.indexOf(variation.toLowerCase());
                if (idx !== -1) {
                  const score = variation.length / word.length;
                  if (score > bestMatch.score) {
                    bestMatch = { index: idx, score, text: variation, matchType: 'partial_word' };
                  }
                }
              }
            }
            
            if (bestMatch.index !== -1) {
              console.log(`✅ Found partial word match: "${bestMatch.text}"`);
              const endIdx = bestMatch.index + bestMatch.text.length;
              const confidence = calculateMatchConfidence(searchText, bestMatch.text, 'partial_word');
              addHighlightPositions(bestMatch.index, endIdx, confidence, 'partial_word');
            }
          }
          
          // Strategy 8: Word proximity search (find words near each other)
          if (positions.length === 0) {
            console.log(`   Trying word proximity search...`);
            const words = searchText.split(/\s+/).filter(w => w.length > 3);
            if (words.length >= 2) {
              const proximityThreshold = 100; // characters
              
              for (let i = 0; i < words.length - 1; i++) {
                const word1 = words[i].toLowerCase();
                const word2 = words[i + 1].toLowerCase();
                
                const idx1 = pageLower.indexOf(word1);
                if (idx1 !== -1) {
                  // Look for second word within proximity
                  const searchStart = Math.max(0, idx1 - proximityThreshold);
                  const searchEnd = Math.min(pageLower.length, idx1 + word1.length + proximityThreshold);
                  const proximityText = pageLower.substring(searchStart, searchEnd);
                  const idx2 = proximityText.indexOf(word2);
                  
                  if (idx2 !== -1) {
                    console.log(`✅ Found proximity match: "${word1}" near "${word2}"`);
                    const actualIdx2 = searchStart + idx2;
                    const startIdx = Math.min(idx1, actualIdx2);
                    const endIdx = Math.max(idx1 + word1.length, actualIdx2 + word2.length);
                    const confidence = calculateMatchConfidence(searchText, word1 + ' ... ' + word2, 'proximity');
                    addHighlightPositions(startIdx, endIdx, confidence, 'proximity');
                    break;
                  }
                }
              }
            }
          }
          
          // Strategy 9: Character-level fuzzy matching for very broken text
          if (positions.length === 0) {
            console.log(`   Trying character-level fuzzy matching...`);
            const minSimilarity = 0.6;
            const searchWindow = searchText.length + 10;
            
            for (let i = 0; i <= fullPageText.length - searchWindow; i += 5) {
              const window = fullPageText.substring(i, i + searchWindow);
              const similarity = calculateStringSimilarity(searchText.toLowerCase(), window.toLowerCase());
              
              if (similarity >= minSimilarity) {
                console.log(`✅ Found fuzzy character match with ${(similarity * 100).toFixed(1)}% similarity`);
                const confidence = calculateMatchConfidence(searchText, window, 'fuzzy_char') * similarity;
                addHighlightPositions(i, i + window.length, confidence, 'fuzzy_char');
                break;
              }
            }
          }
          
          // Strategy 10: Last resort - find any common significant words
          if (positions.length === 0) {
            console.log(`   Trying last resort word search...`);
            const significantWords = searchText.split(/\s+/)
              .filter(word => word.length > 5)
              .filter(word => !/^(the|and|for|with|this|that|from|policy|coverage|insurance|shall|will|may|must)$/i.test(word));
            
            for (const word of significantWords) {
              // Try even more variations
              const variations = [
                word,
                word.toLowerCase(),
                word.replace(/ing$/, ''),
                word.replace(/ed$/, ''),
                word.replace(/s$/, ''),
                word.replace(/ly$/, ''),
                word.substring(0, Math.max(4, word.length - 2)) // Truncated version
              ];
              
              for (const variation of variations) {
                const idx = pageLower.indexOf(variation.toLowerCase());
                if (idx !== -1) {
                  console.log(`✅ Found last resort match: "${variation}"`);
                  const endIdx = idx + variation.length;
                  const confidence = calculateMatchConfidence(searchText, variation, 'last_resort') * 0.4; // Low confidence
                  addHighlightPositions(idx, endIdx, confidence, 'last_resort');
                  break;
                }
              }
              if (positions.length > 0) break;
            }
          }
        }
        }
      }
    
    console.log(`Found ${positions.length} text positions for highlighting`);
    
    // Add context highlighting if enabled
    const enableContext = true; // Can be made configurable
    if (enableContext && positions.length > 0) {
      const contextPositions = findContextPositions(textItems, positions);
      console.log(`Found ${contextPositions.length} context positions`);
      
      // Merge context with main positions, ensuring context doesn't overlap main highlights
      const allPositions = [...positions, ...contextPositions];
      return mergeOverlappingHighlights(allPositions);
    }
    
    return positions;
  } catch (error) {
    console.error('Error searching for text in PDF:', error);
    return [];
  }
};

interface SearchBasedHighlightProps {
  pdfUrl: string;
  citation: Citation;
  pageNumber: number;
  scale: number;
  /** Fires when the matcher finishes — found indicates ≥1 highlight position,
   *  confidence is the best (highest) confidence of the matched positions
   *  (0..1), matchType names the strategy that landed the hit. */
  onMatchInfo?: (info: { found: boolean; confidence: number; matchType?: string; positionCount: number }) => void;
  /** Fires with the first matched position's pixel coordinates so the parent
   *  viewer can scroll the highlight into view. Coordinates are in PDF space
   *  (pre-scale); the parent applies its own scale. */
  onFirstMatchPosition?: (pos: { x: number; y: number; width: number; height: number } | null) => void;
}

// Cache for search results
const searchCache = new Map<string, TextPosition[]>();

// Cache for extracted text content
const textContentCache = new Map<string, any>();

export const SearchBasedPdfHighlight: React.FC<SearchBasedHighlightProps> = ({
  pdfUrl,
  citation,
  pageNumber,
  scale,
  onMatchInfo,
  onFirstMatchPosition,
}) => {
  const onMatchInfoRef = useRef(onMatchInfo);
  const onFirstMatchPositionRef = useRef(onFirstMatchPosition);
  onMatchInfoRef.current = onMatchInfo;
  onFirstMatchPositionRef.current = onFirstMatchPosition;
  const [highlights, setHighlights] = useState<TextPosition[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const pdfDocRef = useRef<any>(null);
  
  // Create cache key from citation details
  const cacheKey = useMemo(
    () => `${pdfUrl}-${citation.page}-${citation.text}`,
    [pdfUrl, citation.page, citation.text]
  );

  // Compute a single confidence/matchType from the highlight positions and
  // notify the parent so the sidebar pill can reflect reality.
  const reportMatchInfo = (positions: TextPosition[]) => {
    const real = positions.filter((p) => !p.isContext);
    if (real.length === 0) {
      onMatchInfoRef.current?.({ found: false, confidence: 0, positionCount: 0 });
      onFirstMatchPositionRef.current?.(null);
      return;
    }
    const best = real.reduce((acc, p) =>
      (p.confidence ?? 0) > (acc.confidence ?? 0) ? p : acc, real[0]);
    onMatchInfoRef.current?.({
      found: true,
      confidence: best.confidence ?? 0.5,
      matchType: best.matchType,
      positionCount: real.length,
    });
    // Use the top-left-most highlight as the scroll target
    const topMost = real.reduce((acc, p) =>
      p.y < acc.y || (p.y === acc.y && p.x < acc.x) ? p : acc, real[0]);
    onFirstMatchPositionRef.current?.({
      x: topMost.x,
      y: topMost.y,
      width: topMost.width,
      height: topMost.height,
    });
  };

  useEffect(() => {
    console.log(`🔍 SearchBasedPdfHighlight effect triggered:`, {
      citationPage: citation.page,
      currentPage: pageNumber,
      citationText: citation.text?.substring(0, 50) + '...',
      pdfUrl: pdfUrl ? 'Available' : 'Missing'
    });

    if (citation.page !== pageNumber) {
      console.log(`⏭️ Skipping highlight - citation on page ${citation.page}, viewing page ${pageNumber}`);
      setHighlights([]);
      // Don't change match-info here — we only know the cite-page status when
      // the user is looking at the cite page. Leaving previous match info
      // alone gives a more honest experience as they flip pages.
      return;
    }

    if (!citation.text || citation.text.trim() === '') {
      console.warn(`⚠️ No citation text to search for:`, citation);
      setHighlights([]);
      reportMatchInfo([]);
      return;
    }

    const searchAndHighlight = async () => {
      console.log(`🔎 Starting text search for: "${citation.text}" on page ${citation.page}`);
      
      // Check cache first
      if (searchCache.has(cacheKey)) {
        console.log(`✨ Using cached search results for: "${citation.text}"`);
        const cachedPositions = searchCache.get(cacheKey)!;
        setHighlights(cachedPositions);
        reportMatchInfo(cachedPositions);
        return;
      }
      
      setIsSearching(true);
      try {
        // Check if citation text is actually searchable
        if (!citation.text || citation.text.includes('[Citation') || citation.text.includes('[Error') || citation.text.includes('[Network')) {
          console.warn(`⚠️ Skipping search for non-searchable citation text: "${citation.text}"`);
          setHighlights([]);
          return;
        }

        // Reuse PDF document if already loaded
        if (!pdfDocRef.current || pdfDocRef.current._pdfUrl !== pdfUrl) {
          const loadingTask = pdfjsLib.getDocument(pdfUrl);
          pdfDocRef.current = await loadingTask.promise;
          pdfDocRef.current._pdfUrl = pdfUrl; // Store URL for comparison
        }
        
        // Search for the cited text
        const positions = await findTextInPdf(
          pdfDocRef.current, 
          citation.text, 
          citation.page
        );
        
        console.log(`📍 Search completed. Found ${positions.length} highlight positions:`, positions);
        
        // Cache the results
        searchCache.set(cacheKey, positions);
        
        // Clean up old cache entries if too many
        if (searchCache.size > 100) {
          const firstKey = searchCache.keys().next().value;
          searchCache.delete(firstKey);
        }
        
        setHighlights(positions);
        reportMatchInfo(positions);

        if (positions.length === 0) {
          console.warn(`❌ No text positions found for citation: "${citation.text}"`);
          console.warn(`   This could mean:
   - Text doesn't exist on this page
   - Text is formatted differently (line breaks, spacing)
   - OCR issues or font encoding problems
   - Text is in images/non-searchable content
   - Document needs re-processing for word spans`);
        } else {
          console.log(`✅ Successfully found ${positions.length} text positions for highlighting`);
        }
      } catch (error) {
        console.error('❌ Error in searchAndHighlight:', error);
        if (error instanceof Error && error.message.includes('worker')) {
          console.error('📋 PDF.js worker failed to load. This could be due to:');
          console.error('   - CORS issues with the CDN');
          console.error('   - Network connectivity problems'); 
          console.error('   - Content Security Policy restrictions');
        }
      } finally {
        setIsSearching(false);
      }
    };
    
    searchAndHighlight();
  }, [pdfUrl, citation, pageNumber]);

  if (citation.page !== pageNumber || highlights.length === 0) {
    return null;
  }

  // Helper function to get confidence-based styling
  const getConfidenceStyle = (confidence: number = 0.5, matchType: string = 'unknown', isContext: boolean = false) => {
    // Context highlighting - subtle gray
    if (isContext) {
      return {
        backgroundColor: 'rgba(156, 163, 175, 0.15)', // Light gray
        border: '1px dashed #9ca3af',
        boxShadow: 'none',
        borderRadius: '2px'
      };
    }
    
    // Special styling for multi-fragment matches
    if (matchType === 'multi_fragment') {
      return {
        backgroundColor: 'rgba(139, 69, 19, 0.2)', // Brown background for fragments
        border: '2px dashed #8b4513', // Dashed border to indicate fragments
        boxShadow: '0 0 6px rgba(139, 69, 19, 0.4)',
        borderRadius: '4px' // Slightly more rounded for fragments
      };
    }
    
    // Special styling for fallback strategies
    if (matchType === 'partial_word') {
      return {
        backgroundColor: 'rgba(168, 85, 247, 0.2)', // Purple for partial word matches
        border: '2px dotted #a855f7',
        boxShadow: '0 0 4px rgba(168, 85, 247, 0.3)',
        borderRadius: '3px'
      };
    }
    
    if (matchType === 'proximity') {
      return {
        backgroundColor: 'rgba(20, 184, 166, 0.2)', // Teal for proximity matches
        border: '2px dotted #14b8a6',
        boxShadow: '0 0 4px rgba(20, 184, 166, 0.3)',
        borderRadius: '3px'
      };
    }
    
    if (matchType === 'fuzzy_char') {
      return {
        backgroundColor: 'rgba(244, 63, 94, 0.2)', // Rose for character-level fuzzy
        border: '2px dotted #f43f5e',
        boxShadow: '0 0 4px rgba(244, 63, 94, 0.3)',
        borderRadius: '3px'
      };
    }
    
    if (matchType === 'last_resort') {
      return {
        backgroundColor: 'rgba(107, 114, 128, 0.2)', // Gray for last resort
        border: '2px dotted #6b7280',
        boxShadow: '0 0 3px rgba(107, 114, 128, 0.2)',
        borderRadius: '2px'
      };
    }
    
    if (confidence >= 0.9) {
      return {
        backgroundColor: 'rgba(76, 175, 80, 0.3)', // Green for high confidence
        border: '2px solid #4caf50',
        boxShadow: '0 0 6px rgba(76, 175, 80, 0.4)'
      };
    } else if (confidence >= 0.7) {
      return {
        backgroundColor: 'rgba(255, 235, 59, 0.4)', // Yellow for medium confidence  
        border: '2px solid #f59e0b',
        boxShadow: '0 0 4px rgba(245, 158, 11, 0.3)'
      };
    } else {
      return {
        backgroundColor: 'rgba(255, 152, 0, 0.3)', // Orange for low confidence
        border: '2px solid #ff9800',
        boxShadow: '0 0 4px rgba(255, 152, 0, 0.3)'
      };
    }
  };

  // Suppress visual highlights when the matcher's best-effort fragment match
  // is below 75% confidence. The sidebar pill still reports the real number,
  // so users see honest "Fuzzy match · 62%" without a misleading yellow box
  // painted on text that may not actually be the cited content.
  const MIN_VISIBLE_CONFIDENCE = 0.75;
  const visibleHighlights = highlights.filter(
    (pos) => pos.isContext || (pos.confidence ?? 0) >= MIN_VISIBLE_CONFIDENCE
  );

  return (
    <>
      {visibleHighlights.map((pos, idx) => (
        <div
          key={`highlight-${idx}`}
          className={`pdf-text-highlight ${pos.isContext ? 'context-highlight' : ''}`}
          style={{
            position: 'absolute',
            left: `${pos.x * scale}px`,
            top: `${pos.y * scale}px`,
            width: `${pos.width * scale}px`,
            height: `${pos.height * scale}px`,
            ...getConfidenceStyle(pos.confidence, pos.matchType, pos.isContext),
            pointerEvents: 'none',
            zIndex: pos.isContext ? 8 : 10, // Context behind main highlights
            animation: pos.isContext ? 'none' : 'highlightPulse 2s ease-in-out'
          }}
          title={pos.isContext 
            ? 'Context around citation' 
            : `"${citation.text}" (${Math.round((pos.confidence || 0.5) * 100)}% confidence, ${pos.matchType || 'unknown'} match)`}
        />
      ))}
      {isSearching && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            backgroundColor: 'rgba(59, 130, 246, 0.9)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '4px',
            fontSize: '12px',
            zIndex: 20
          }}
        >
          Searching for text...
        </div>
      )}
    </>
  );
};

// Helper function to merge overlapping highlights
export const mergeOverlappingHighlights = (positions: TextPosition[]): TextPosition[] => {
  if (positions.length <= 1) return positions;
  
  // Sort by x position
  const sorted = [...positions].sort((a, b) => a.x - b.x);
  const merged: TextPosition[] = [];
  
  let current = sorted[0];
  
  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    
    // Check if positions overlap or are very close
    if (current.y === next.y && 
        current.x + current.width >= next.x - 2) {
      // Merge positions
      current = {
        ...current,
        width: Math.max(current.x + current.width, next.x + next.width) - current.x
      };
    } else {
      merged.push(current);
      current = next;
    }
  }
  
  merged.push(current);
  return merged;
};